import { FC, useState } from 'react';
import {
  Badge,
  Box,
  IconButton,
  Popover,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import CTAButton from '@/components/ui/CTAButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import useNotificationStore from '@/store/notifications';
import { AppNotification } from '@/types';
import { Routes } from '@/constants/routes';
import { getPath } from '@/utils';

const typeIcons: Record<string, string> = {
  contact_assigned: '\u{1F464}',
  case_uebergabe: '\u{1F464}',
  meeting_reminder: '\u{1F4C5}',
  meeting_outcome: '\u2705',
  deal_status_changed: '\u{1F4CA}',
  followup_due: '\u23F0',
  contact_recycled: '\u267B\uFE0F',
  customer_document_uploaded: '\u{1F4CE}',
  admin_alarm: '\u26A0\uFE0F',
  feedback_erledigt: '\u{1F4AC}',
};

const NotificationBell: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead, fetchNotifications } =
    useNotificationStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => setAnchorEl(null);

  const handleClick = async (notification: AppNotification) => {
    if (!notification.read_at) {
      await markAsRead(notification.id);
    }
    handleClose();
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  const handleViewAll = () => {
    handleClose();
    navigate(`/${getPath(Routes.Home, Routes.Notifications)}`);
  };

  const recent = notifications.slice(0, 10);

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          color: 'navigation.gedimmt',
          p: 2,
          borderRadius: '8px',
          '&:hover': { bgcolor: 'background.navigationAktiv' },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          max={99}
          invisible={unreadCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              typography: 'label',
              minWidth: 16,
              height: 16,
              px: 0.5,
              bgcolor: 'error.main',
              color: 'primary.contrastText',
              border: '1px solid',
              borderColor: 'background.navigation',
              top: 1,
              right: 1,
            },
          }}
        >
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            sx={{ width: 20, height: 20 }}
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </Box>
        </Badge>
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 480 } } }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          px={4}
          py={2}
        >
          <Typography variant="seitentitel">
            {t('notifications:title')}
          </Typography>
          {unreadCount > 0 && (
            <CTAButton
              variant="tertiary"
              size="small"
              autoWidth
              label={t('notifications:markAllRead')}
              onClick={() => markAllAsRead()}
            />
          )}
        </Stack>
        <Divider />
        {recent.length === 0 ? (
          <Typography variant="meta" p={6} textAlign="center">
            {t('notifications:empty')}
          </Typography>
        ) : (
          <List disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
            {recent.map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => handleClick(n)}
                sx={{
                  bgcolor: n.read_at ? 'transparent' : 'primary.hover',
                  '&:hover': { bgcolor: 'primary.hover' },
                  px: 4,
                  py: 2,
                }}
              >
                {/* Emoji-Symbol in Icon-Groesse (20px), keine Textrolle. */}
                <Box sx={{ mr: 2, fontSize: 20 }}>{typeIcons[n.type] || '\u{1F514}'}</Box>
                <ListItemText
                  primary={n.title}
                  secondary={`${n.message} \u00B7 ${moment(n.created_at).fromNow()}`}
                  primaryTypographyProps={{
                    variant: n.read_at ? 'wert' : 'wertBetont',
                    noWrap: true,
                  }}
                  secondaryTypographyProps={{
                    variant: 'klein',
                    noWrap: true,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
        <Divider />
        <CTAButton
          variant="tertiary"
          label={t('notifications:viewAll')}
          onClick={handleViewAll}
        />
      </Popover>
    </>
  );
};

export default NotificationBell;
